/**
 * Custom error classes for datepicker availability API operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { BaseApiFetchError } from '../../utility/error/BaseApiFetchError'
import { BaseApiValidationError } from '../../utility/error/BaseApiValidationError'

export class DatepickerFetchError extends BaseApiFetchError {
  public readonly slug: string
  public readonly categories: string[]
  public readonly pitchtypeId?: string

  constructor(
    url: string,
    slug: string,
    categories: string[],
    pitchtypeId: string | undefined,
    originalError: any,
  ) {
    // Create params object from datepicker-specific parameters
    const params = { slug, categories, pitchtypeId }
    super('datepicker-availability', url, params, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'DatepickerFetchError',
      enumerable: true,
    })
    this.slug = slug
    this.categories = categories
    this.pitchtypeId = pitchtypeId
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   * Extends base context with datepicker-specific fields for backwards compatibility
   */
  override getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add datepicker-specific context fields for backwards compatibility
      slug: this.slug,
      categories: JSON.stringify(this.categories),
      pitchtypeId: this.pitchtypeId,
    }
  }
}

export class DatepickerValidationError extends BaseApiValidationError {
  public readonly slug: string
  public readonly categories: string[]
  public readonly pitchtypeId?: string

  constructor(
    url: string,
    slug: string,
    categories: string[],
    pitchtypeId: string | undefined,
    rawResponse: any,
    originalError: any,
  ) {
    // Create params object from datepicker-specific parameters
    const params = { slug, categories, pitchtypeId }
    super('datepicker-availability', url, params, rawResponse, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'DatepickerValidationError',
      enumerable: true,
    })
    this.slug = slug
    this.categories = categories
    this.pitchtypeId = pitchtypeId
  }

  /**
   * Get context data for Sentry or logging
   * Extends base context with datepicker-specific fields for backwards compatibility
   */
  getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add datepicker-specific context fields for backwards compatibility
      slug: this.slug,
      categories: JSON.stringify(this.categories),
      pitchtypeId: this.pitchtypeId,
    }
  }
}
